import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterListIcon from "@mui/icons-material/FilterList";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Backdrop, Box, Button, CircularProgress, Collapse, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip, debounce } from "@mui/material";
import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { AdminUserSearchDto, AdminUserSearchDtoProject, AdminUserSearchDtoTool } from "../api/models/adminUserSearchDto";
import { getColumns } from "./getAdminUsersGridColumns";
import AdvancedFiltersDialog from "../../../Common/components/AdvancedFiltersDialog/AdvancedFiltersDialog";
import {
  AdvancedFiltersState,
  defaultAdvancedFiltersState,
  AdvancedFilterOptions,
  buildAdvancedFiltersDto,
} from "../../../Common/models/advancedFilters";
import { ADMIN_USERS_FILTER_CONFIG } from "../../../Common/models/advancedFilterConfig";
import { useAdminAdvancedFiltersSearch } from "../hooks/useAdminAdvancedFiltersSearch";
import { getUsersByAdvancedFilters, exportUsersByAdvancedFilters } from "../api/adminUserSearchClient";

const ProjectItem = ({ project, expanded, onToggle }: { project: AdminUserSearchDtoProject, expanded: boolean, onToggle: () => void }) => {
  const hasTools = project.tools && project.tools.length > 0;

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {hasTools ? (
          <IconButton
            onClick={onToggle}
            size="small"
            sx={{
              p: 0,
              mr: 1,
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 9l6 6l6 -6" />
            </svg>
          </IconButton>
        ) : (
          <Box sx={{ width: 20, height: 20, mr: 1 }} />
        )}
        <Tooltip title={hasTools ? project.tools.join(", ") : ""}>
          <a href={`/project/${project.projectId}/details`}>
            {project.isArchived ? <s>{project.projectName}</s> : project.projectName} - {project.roleName}
          </a>
        </Tooltip>
      </Box>
      {hasTools && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ pl: 4, fontSize: "0.875rem", color: "text.secondary" }}>
            {project.tools.map((tool, index) => (
              <div key={index}>{tool}</div>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const ToolItem = ({ tool, expanded, onToggle }: { tool: AdminUserSearchDtoTool, expanded: boolean, onToggle: () => void }) => {
  const hasProjects = tool.projects && tool.projects.length > 0;

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {hasProjects ? (
          <IconButton
            onClick={onToggle}
            size="small"
            sx={{
              p: 0,
              mr: 1,
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 9l6 6l6 -6" />
            </svg>
          </IconButton>
        ) : (
          <Box sx={{ width: 20, height: 20, mr: 1 }} />
        )}
        <Tooltip title={
          hasProjects ? (
            <div>
              {tool.projects.map((p, i) => (
                <div key={i}>{p.projectName} {p.customRole && `(${p.customRole})`}</div>
              ))}
            </div>
          ) : ""
        }>
          <span>
            {tool.toolName} {tool.customRoles.length > 0 && `(${tool.customRoles.join(", ")})`}
          </span>
        </Tooltip>
      </Box>
      {hasProjects && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ pl: 4, fontSize: "0.875rem", color: "text.secondary" }}>
            {tool.projects.map((p, index) => (
              <div key={index}>{p.projectName} {p.customRole && `(${p.customRole})`}</div>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

interface AdminUsersGridProps {}

type Order = "asc" | "desc";

const AdminUsersGrid = (): JSX.Element => {
  const columns = getColumns();

  const [users, setUsers] = useState<AdminUserSearchDto[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [order, setOrder] = useState("asc" as Order);
  const [orderBy, setOrderBy] = useState("adPrinciple" as keyof AdminUserSearchDto);

  // Advanced filters
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFiltersState>(defaultAdvancedFiltersState);
  const advancedFiltersRef = useRef<AdvancedFiltersState>(defaultAdvancedFiltersState);
  const { data: filterOptions, isLoading: isFilterOptionsLoading } = useAdminAdvancedFiltersSearch();

  useEffect(() => {
    advancedFiltersRef.current = advancedFilters;
  }, [advancedFilters]);

  const hasActiveFilters =
    advancedFilters.selectedPackages.length > 0 ||
    advancedFilters.selectedTools.length > 0 ||
    advancedFilters.selectedRoles.length > 0

  const buildDto = (filters: AdvancedFiltersState): AdvancedFilterOptions =>
    buildAdvancedFiltersDto(filters);

  const fetchUsers = async (searchValue: string, filters: AdvancedFiltersState) => {
    setIsSearching(true);
    try {
      const results = await getUsersByAdvancedFilters(buildDto(filters), searchValue);
      setUsers(results);
    } catch {
      // keep current users on error
    } finally {
      setIsSearching(false);
    }
  };

  const handleApplyAdvancedFilters = async (filters: AdvancedFiltersState) => {
    setAdvancedFilters(filters);
    const currentSearch: string = searchInputRef.current?.["value"] ?? "";
    await fetchUsers(currentSearch, filters);
  };

  const compare = (a: string, b: string): number => {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  }

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof AdminUserSearchDto,
    direction?: Order
  ) => {
    if (!direction) {
      if (orderBy == property)
        direction = order == "asc" ? "desc" : "asc"
      else
        direction = "asc";
    }

    setOrder(direction);
    setOrderBy(property);

    users.sort((a: AdminUserSearchDto, b: AdminUserSearchDto) => {
      return compare(a[property] as string, b[property] as string) * (direction == "desc" ? -1 : 1);
    });
    setUsers(users);
  };

  const createSortHandler =
    (property: keyof AdminUserSearchDto) => (event: React.MouseEvent<unknown>) => {
      handleRequestSort(event, property);
    };

  const searchInputRef = useRef();

  const performSearch = () => {
    const inputValue: string = searchInputRef.current!["value"];
    if (inputValue.length < 3 && inputValue.length !== 0)
      return;
    fetchUsers(inputValue, advancedFiltersRef.current);
    handleRequestSort(null as any, "name", "asc");
  };

  const debouncedHandleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key == "Enter" || e.key == "Tab")
      return;

    debouncedCallback();
  };

  const debouncedCallback = useCallback(debounce(() => {
    performSearch();
  }, 500), []);

  const [expandedProjectKeys, setExpandedProjectKeys] = useState<Set<string>>(new Set());
  const [expandedToolKeys, setExpandedToolKeys] = useState<Set<string>>(new Set());

  const allProjectKeys = useMemo(() => {
    return new Set(users.flatMap(u => u.projects.map(p => `${u.adPrinciple}_${p.projectId}`)));
  }, [users]);

  const allToolKeys = useMemo(() => {
    return new Set(users.flatMap(u => u.tools.map(t => `${u.adPrinciple}_${t.toolName}`)));
  }, [users]);

  const toggleProject = (key: string) => {
    const newSet = new Set(expandedProjectKeys);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setExpandedProjectKeys(newSet);
  };

  const toggleTool = (key: string) => {
    const newSet = new Set(expandedToolKeys);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setExpandedToolKeys(newSet);
  };

  const toggleAllProjects = () => {
    if (expandedProjectKeys.size === allProjectKeys.size) {
      setExpandedProjectKeys(new Set());
    } else {
      setExpandedProjectKeys(allProjectKeys);
    }
  };

  const toggleAllTools = () => {
    if (expandedToolKeys.size === allToolKeys.size) {
      setExpandedToolKeys(new Set());
    } else {
      setExpandedToolKeys(allToolKeys);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const currentSearch: string = searchInputRef.current?.["value"] ?? "";
      await exportUsersByAdvancedFilters(buildDto(advancedFiltersRef.current), currentSearch);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isSearching || isExporting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box sx={{ p: "24px", height: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            mb: 3,
          }}
        >
          <IconButton
            type="submit"
            sx={{ p: "5px", marginTop: "14px" }}
            aria-label="search"
            onClick={performSearch}
          >
            <SearchRoundedIcon />
          </IconButton>
          <TextField
            sx={{
              width: 450,
              mr: 1,
              "& .MuiFormLabel-root.MuiInputLabel-root": {
                color: "rgba(0, 0, 0, 0.38)",
              },
              "& .MuiInputBase-root.MuiInput-root.MuiInput-underline:before": {
                borderBottom: "1px solid rgba(0, 0, 0, 0.38)",
              },
            }}
            type="search"
            variant="standard"
            label={"Search name, Heiway account, email"}
            onKeyDown={debouncedHandleSearchKeyDown}
            inputRef={searchInputRef}
          />
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            endIcon={hasActiveFilters ? <WarningAmberIcon sx={{ color: "#f9a825" }} /> : undefined}
            onClick={() => setAdvancedFiltersOpen(true)}
            sx={{ ml: 2, alignSelf: "flex-end" }}
          >
            Advanced Filters
          </Button>
          {users.length > 0 && (
            <Button
              variant="contained"
              onClick={handleExport}
              sx={{ ml: "auto", alignSelf: "flex-end" }}
            >
              Export to CSV
            </Button>
          )}
        </Stack>
        <AdvancedFiltersDialog
          open={advancedFiltersOpen}
          filters={advancedFilters}
          filterOptions={filterOptions}
          filterConfig={ADMIN_USERS_FILTER_CONFIG}
          isLoading={isFilterOptionsLoading}
          onApply={handleApplyAdvancedFilters}
          onClose={() => setAdvancedFiltersOpen(false)}
        />
        <TableContainer
          component={Paper}
          sx={{ width: "100%", paddingBottom: 0, marginBottom: "70px" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((headCell) => (
                  <TableCell sx={{ width: headCell.width }} >
                    {headCell.sortable &&
                      <TableSortLabel
                        active={orderBy === headCell.field}
                        direction={orderBy === headCell.field ? order : "asc"}
                        onClick={createSortHandler(headCell.field as keyof AdminUserSearchDto)}>
                        <b>{headCell.headerName}</b>
                      </TableSortLabel>
                    }
                    {!headCell.sortable && (
                      headCell.field === 'projects' ? (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            onClick={toggleAllProjects}
                            size="small"
                            sx={{
                              p: 0,
                              mr: 1,
                              transform: expandedProjectKeys.size === allProjectKeys.size && allProjectKeys.size > 0 ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 0.3s",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M6 9l6 6l6 -6" />
                            </svg>
                          </IconButton>
                          <b>{headCell.headerName}</b>
                        </Box>
                      ) : headCell.field === 'tools' ? (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            onClick={toggleAllTools}
                            size="small"
                            sx={{
                              p: 0,
                              mr: 1,
                              transform: expandedToolKeys.size === allToolKeys.size && allToolKeys.size > 0 ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 0.3s",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M6 9l6 6l6 -6" />
                            </svg>
                          </IconButton>
                          <b>{headCell.headerName}</b>
                        </Box>
                      ) : (
                        <b>{headCell.headerName}</b>
                      )
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user: AdminUserSearchDto) => {
                return (
                  <TableRow>
                    <TableCell>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {user.name}
                      </div>
                    </TableCell>
                    <TableCell> {user.adPrinciple}</TableCell>
                    <TableCell> {user.email}</TableCell>
                    <TableCell>
                      {user.projects.map((project: AdminUserSearchDtoProject) => {
                        const key = `${user.adPrinciple}_${project.projectId}`;
                        return (
                          <ProjectItem
                            key={project.projectId}
                            project={project}
                            expanded={expandedProjectKeys.has(key)}
                            onToggle={() => toggleProject(key)}
                          />
                        );
                      })}
                    </TableCell>
                    <TableCell>
                      {user.tools.map((tool: AdminUserSearchDtoTool) => {
                        const key = `${user.adPrinciple}_${tool.toolName}`;
                        return (
                          <ToolItem
                            key={tool.toolName}
                            tool={tool}
                            expanded={expandedToolKeys.has(key)}
                            onToggle={() => toggleTool(key)}
                          />
                        );
                      })}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default AdminUsersGrid;
